/*
  Smart DM strings shared by the server, which sends the DM, and the clients,
  which preview it — one source so a preview can never show a fan something
  other than what the worker sends. Placeholders accept single or double braces
  with optional inner spaces, the code-format syntax creators already know.
*/

const _ = require('lodash');
const { toPublicImageUrl } = require('./s3');

const DEFAULT_LINK_DM_TEMPLATE = "Thanks for commenting! Here's the link to {product name}: {link}";
const DEFAULT_UNNAMED_LINK_DM_TEMPLATE = "Thanks for commenting! Here's the link: {link}";
const DEFAULT_COLLECTION_DM_TEMPLATE = "Thanks for commenting! Here's the collection link: {link}";
const DEFAULT_STORY_LINK_DM_TEMPLATE = "Here's the link to {product name}: {link}";
const DEFAULT_UNNAMED_STORY_LINK_DM_TEMPLATE = "Here's the link: {link}";
const DEFAULT_STORY_COLLECTION_DM_TEMPLATE = "Here's the collection link: {link}";
const DM_TEMPLATE_MAX_LENGTH = 500;
const BUTTON_LABEL_MAX_LENGTH = 20;
const DEFAULT_LINK_BUTTON_LABEL = 'Shop';
const DEFAULT_COLLECTION_BUTTON_LABEL = 'View collection';
const SMART_DM_REQUIRED_SCOPES = ['instagram_business_manage_comments', 'instagram_business_manage_messages'];
// SocialAutoDmSends.sourceType values for a story. A post send stays `comment`.
const STORY_SOURCES = ['story_reply', 'story_mention'];
// scope is the comma-joined string an Instagram account row stores.
const hasSmartDmScopes = scope => {
  const heldScopes = (scope || '').split(',').map(held => held.trim());
  return SMART_DM_REQUIRED_SCOPES.every(required => heldScopes.includes(required));
};

// Meta replaces the previous subscribed_fields list, so messages is added beside comments.
// Null when the token cannot receive comments, and comments alone when Smart DM is off.
const getInstagramSubscribedFields = (scope, { smartDmEnabled = false } = {}) => {
  const heldScopes = (scope || '').split(',').map(held => held.trim());
  if (!heldScopes.includes('instagram_business_manage_comments')) return null;
  if (smartDmEnabled === true && heldScopes.includes('instagram_business_manage_messages')) return 'comments,messages';
  return 'comments';
};

const LINK_PLACEHOLDER_PATTERN = '\\{\\{\\s*link\\s*\\}\\}|\\{\\s*link\\s*\\}';
// {name}, {product name}, and {collection name} all render the destination's name — the qualified
// forms read better in the editor; the bare form keeps older saved templates working.
const NAME_PLACEHOLDER_PATTERN = '\\{\\{\\s*(?:product\\s+|collection\\s+)?name\\s*\\}\\}|\\{\\s*(?:product\\s+|collection\\s+)?name\\s*\\}';

// A link with no title gets the nameless variant rather than "the link to it".
// A story fan did not comment, so the blank fallback leaves that out.
const getDefaultDmTemplate = (destinationType, { hasName = true, source } = {}) => {
  if (source === 'story') {
    if (destinationType === 'collection') return DEFAULT_STORY_COLLECTION_DM_TEMPLATE;
    return hasName ? DEFAULT_STORY_LINK_DM_TEMPLATE : DEFAULT_UNNAMED_STORY_LINK_DM_TEMPLATE;
  }
  if (destinationType === 'collection') return DEFAULT_COLLECTION_DM_TEMPLATE;
  return hasName ? DEFAULT_LINK_DM_TEMPLATE : DEFAULT_UNNAMED_LINK_DM_TEMPLATE;
};

const templateHasLinkPlaceholder = template => new RegExp(LINK_PLACEHOLDER_PATTERN).test(template || '');

// Replacer functions so $-sequences in creator-controlled names ("Save $$$") stay literal.
const renderDmTemplate = (template, { link, name }) =>
  template.replace(new RegExp(NAME_PLACEHOLDER_PATTERN, 'g'), () => name || 'it').replace(new RegExp(LINK_PLACEHOLDER_PATTERN, 'g'), () => link);

// The rendered body always carries the link, even when a saved template lost its placeholder.
const renderDmTemplateWithLink = (template, values) => {
  const body = renderDmTemplate(template, values);
  return body.includes(values.link) ? body : `${body} ${values.link}`.trim();
};

// The save rule, worded once: the settings route rejects with it and the editors show it inline.
// Null or empty means "use the house copy", so it passes.
const getDmTemplateError = template => {
  if (template == null || template === '') return null;
  if (typeof template !== 'string' || template.length > DM_TEMPLATE_MAX_LENGTH) return `Reply message must be ${DM_TEMPLATE_MAX_LENGTH} characters or fewer.`;
  if (!templateHasLinkPlaceholder(template)) return 'Reply message must include the {link} placeholder.';
  return null;
};

// Length is the trimmed label. Surrounding spaces are not part of it.
const getButtonLabelError = label => {
  if (label == null || (typeof label === 'string' && !label.trim())) return "Button label can't be empty.";
  if (typeof label !== 'string' || label.trim().length > BUTTON_LABEL_MAX_LENGTH) {
    return `Button label must be ${BUTTON_LABEL_MAX_LENGTH} characters or fewer.`;
  }
  return null;
};

// Blank reads as the default. The column default is that same text.
const buttonLabelOrDefault = (stored, fallback) => (typeof stored === 'string' && stored.trim()) || fallback;

const REPLY_FORMATS = ['card', 'text'];
const DEFAULT_REPLY_FORMAT = 'card';
const COMMENT_REPLY_MAX_COUNT = 8;
const COMMENT_REPLY_MAX_LENGTH = 200;
// A public comment sits on the post. The tracked link belongs in the DM.
// The last label stays a known TLD, so "Just sent.Thanks" is not a domain.
// A host ending in letters and a slash is a link even when that TLD is not listed, so "$4.99/mo" stays a price.
const COMMENT_REPLY_LINK_PATTERN = /:\/\/|www\.|\b(?:[a-z0-9-]+\.)+(?:com|net|org|us|co|io|me|ly|link|app|shop|to|gl|be)\b|\b[a-z0-9-]+(?:\.[a-z0-9-]+)*\.[a-z]{2,}\//i;

const replyFormatOrDefault = stored => (stored === 'text' ? 'text' : DEFAULT_REPLY_FORMAT);

const getReplyFormatError = format => (REPLY_FORMATS.includes(format) ? null : 'Reply format must be card or text.');

// Null, empty, a JSON string, or an array. Empty strings drop out. Null means the value is not a list.
const normalizeCommentReplies = value => {
  if (value == null || value === '') return [];
  let list = value;
  if (typeof value === 'string') {
    try {
      list = JSON.parse(value);
    } catch {
      return null;
    }
  }
  if (!Array.isArray(list)) return null;
  return list.map(item => (typeof item === 'string' ? item.trim() : item)).filter(item => item !== '');
};

const parseCommentReplies = stored => normalizeCommentReplies(stored) || [];

const getCommentRepliesError = replies => {
  const normalized = normalizeCommentReplies(replies);
  if (!normalized) return 'Comment replies must be a list of messages.';
  if (normalized.length > COMMENT_REPLY_MAX_COUNT) return `Comment replies must be ${COMMENT_REPLY_MAX_COUNT} or fewer.`;
  for (const reply of normalized) {
    if (typeof reply !== 'string') return 'Each comment reply must be text.';
    if (reply.length > COMMENT_REPLY_MAX_LENGTH) return `Each comment reply must be ${COMMENT_REPLY_MAX_LENGTH} characters or fewer.`;
    if (COMMENT_REPLY_LINK_PATTERN.test(reply)) return "Comment replies can't include a link.";
  }
  return null;
};

// true, false, or null when the value is neither on nor off.
const commentReplyChoice = enabled => {
  if (enabled === true || enabled === 1 || enabled === '1' || enabled === 'true') return true;
  if (enabled === false || enabled === 0 || enabled === '0' || enabled === 'false') return false;
  return null;
};

const commentRepliesEnabled = enabled => commentReplyChoice(enabled) === true;

// The next reply after the one last posted. Different choices do not post back to back.
const pickCommentReply = (replies, lastSentText) => {
  const list = parseCommentReplies(replies);
  if (!list.length) return null;
  const start = list.lastIndexOf(lastSentText);
  for (let step = 1; step <= list.length; step++) {
    const reply = list[(start + step) % list.length];
    if (reply !== lastSentText) return reply;
  }
  return list[0];
};

// Mirrors the public collection read: "Hidden" (private) collections still open from their link;
// only an archived private collection 404s for anyone but the owner. Smart DM pauses only then.
const isCollectionLinkReachable = collection => !(collection?.isArchived && collection?.private);

// Gone = once matched a Product and last seen as not a product page, whether the row is still parsed
// (the latest clean crawl was a 404 or a redirect away) or has climbed to dead. Product_id rules out a
// live /collections/ page (flagged not-a-product each crawl); a bot wall or 5xx sets lastErrorClass instead.
const isCrawlFetchError = lastErrorClass => lastErrorClass === 'blocked' || lastErrorClass === 'transient';
const isPinLinkReachable = urlData => {
  const isGoneProduct =
    (urlData?.status === 'dead' || urlData?.status === 'parsed') &&
    !!urlData.Product_id &&
    (urlData.isProductPage === false || urlData.isProductPage === 0);
  return !isGoneProduct || isCrawlFetchError(urlData.lastErrorClass);
};

// search_instagram folds REELS into media_type; media_product_type is Instagram's own field.
const INSTAGRAM_MEDIA_TYPE_LABELS = { REELS: 'Reel', VIDEO: 'Video', CAROUSEL_ALBUM: 'Post carousel', IMAGE: 'Post', STORY: 'Story' };
const getInstagramMediaTypeLabel = media =>
  INSTAGRAM_MEDIA_TYPE_LABELS[media?.media_product_type === 'REELS' ? 'REELS' : media?.media_type || ''] || 'Post';

const SMART_DM_GATES = {
  no_instagram: {
    title: 'Connect Instagram to get started',
    body: 'Anyone who comments on a connected post asking for the link gets it by DM, automatically.',
    button: 'Connect Instagram'
  },
  legacy_facebook_login: {
    title: 'Reconnect Instagram to enable ShopMy DM',
    body: "Your Instagram is connected through Facebook. ShopMy DM needs Instagram's direct connection — reconnecting takes a minute and keeps your links as they are.",
    button: 'Reconnect Instagram'
  },
  missing_scopes: {
    title: 'ShopMy DM needs two more Instagram permissions',
    body: 'Comments and messages — to read link requests on your posts and reply by DM. Reconnect and keep both switched on.',
    button: 'Reconnect Instagram'
  }
};
const getSmartDmGate = eligibility => SMART_DM_GATES[eligibility] || null;

// Meta fetches the card image itself. The hosted S mark stands in when the pin and the catalog have nothing it can fetch.
const SMART_DM_CARD_FALLBACK_IMAGE = 'https://static.shopmy.us/logo/shop_my_s_black.png';
// The card title when a pin has no title and no brand. The id stays in the URL.
const SMART_DM_CARD_FALLBACK_TITLE = 'Shop this';
// A collection with no name on the creator's feed row.
const SMART_DM_UNTITLED_COLLECTION = 'Untitled collection';
const CARD_TEXT_MAX = 80;

const truncateCardText = value => _.truncate(String(value ?? '').trim(), { length: CARD_TEXT_MAX, omission: '…' });

// Graph's story urls expire within hours, and the still saved at connect is a private S3 object.
// A connected story with no still uses the hosted mark. An unconnected story keeps the url Graph just returned.
const storyThumbnailUrl = ({ thumbnailUrl, mediaSnapshotUrl, connected = false } = {}) => {
  const still = toPublicImageUrl(mediaSnapshotUrl);
  if (still) return still;
  if (connected) return SMART_DM_CARD_FALLBACK_IMAGE;
  return (typeof thumbnailUrl === 'string' && thumbnailUrl.trim()) || null;
};

// A non-http value cannot be the card image. Meta fetches it.
const fetchableCardImage = url => {
  const publicUrl = toPublicImageUrl(url);
  if (publicUrl && /^https?:\/\//i.test(publicUrl)) return publicUrl;
  return null;
};

const firstFetchableImage = (...urls) => {
  for (const url of urls) {
    const publicUrl = fetchableCardImage(url);
    if (publicUrl) return publicUrl;
  }
  return SMART_DM_CARD_FALLBACK_IMAGE;
};

// Meta requires a title, so a nameless card promotes its subtitle rather than inventing a name.
const buildCard = ({ kind, title, subtitle, image, url }) => {
  const name = truncateCardText(title);
  const text = truncateCardText(subtitle);
  const card = { kind, image, title: name || text || SMART_DM_CARD_FALLBACK_TITLE, url };
  if (name && text) card.subtitle = text;
  return card;
};

module.exports = {
  DEFAULT_LINK_DM_TEMPLATE,
  DEFAULT_UNNAMED_LINK_DM_TEMPLATE,
  DEFAULT_COLLECTION_DM_TEMPLATE,
  DEFAULT_STORY_LINK_DM_TEMPLATE,
  DEFAULT_UNNAMED_STORY_LINK_DM_TEMPLATE,
  DEFAULT_STORY_COLLECTION_DM_TEMPLATE,
  DM_TEMPLATE_MAX_LENGTH,
  BUTTON_LABEL_MAX_LENGTH,
  DEFAULT_LINK_BUTTON_LABEL,
  DEFAULT_COLLECTION_BUTTON_LABEL,
  REPLY_FORMATS,
  SMART_DM_REQUIRED_SCOPES,
  STORY_SOURCES,
  hasSmartDmScopes,
  getInstagramSubscribedFields,
  LINK_PLACEHOLDER_PATTERN,
  NAME_PLACEHOLDER_PATTERN,
  getDefaultDmTemplate,
  getDmTemplateError,
  getButtonLabelError,
  buttonLabelOrDefault,
  COMMENT_REPLY_MAX_COUNT,
  COMMENT_REPLY_MAX_LENGTH,
  replyFormatOrDefault,
  getReplyFormatError,
  normalizeCommentReplies,
  parseCommentReplies,
  getCommentRepliesError,
  commentReplyChoice,
  commentRepliesEnabled,
  pickCommentReply,
  renderDmTemplateWithLink,
  templateHasLinkPlaceholder,
  renderDmTemplate,
  getInstagramMediaTypeLabel,
  isCollectionLinkReachable,
  isPinLinkReachable,
  getSmartDmGate,
  SMART_DM_CARD_FALLBACK_IMAGE,
  SMART_DM_CARD_FALLBACK_TITLE,
  SMART_DM_UNTITLED_COLLECTION,
  truncateCardText,
  storyThumbnailUrl,
  fetchableCardImage,
  firstFetchableImage,
  buildCard
};
